import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import productService, { Product } from '../services/productService';
import stockMovementService, { TransferStockData, CreateWarehouseData } from '../services/stockMovementService';

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px',
  background: '#f4efe2', border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4, fontFamily: "'Inter', sans-serif",
  fontSize: 12, color: '#5b5a52', outline: 'none', boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500,
  letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c',
  display: 'block', marginBottom: 6,
};

export const StockTransferPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedProductId, setSelectedProductId] = useState('');
  const [fromWarehouseId, setFromWarehouseId] = useState('');
  const [toWarehouseId, setToWarehouseId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [notes, setNotes] = useState('');
  const [availableStock, setAvailableStock] = useState<number>(0);
  const [showWarehouseModal, setShowWarehouseModal] = useState(false);
  const [newWarehouseName, setNewWarehouseName] = useState('');
  const [newWarehouseCode, setNewWarehouseCode] = useState('');

  const { data: warehouses = [], isLoading: isLoadingWarehouses } = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => stockMovementService.getWarehouses(),
  });

  const { data: products = [], isLoading: isLoadingProducts } = useQuery({
    queryKey: ['products-inventory'],
    queryFn: async () => {
      const response = await productService.getProducts();
      const allProducts = response.data || [];
      return allProducts.filter((p: Product) => p.itemType === 'INVENTORY');
    },
  });

  const { isLoading: isLoadingStock } = useQuery({
    queryKey: ['warehouse-stock', fromWarehouseId, selectedProductId],
    queryFn: async () => {
      if (!fromWarehouseId) return null;
      const data = await stockMovementService.getWarehouseStock(fromWarehouseId, 'INVENTORY');
      const stockItem = data.stock.find((s) => s.productId === selectedProductId);
      setAvailableStock(stockItem?.quantityOnHand || 0);
      return data;
    },
    enabled: !!fromWarehouseId && !!selectedProductId,
  });

  const transferMutation = useMutation({
    mutationFn: (data: TransferStockData) => stockMovementService.transferStock(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouse-stock'] });
      alert('Stock transferred successfully!');
      setSelectedProductId('');
      setFromWarehouseId('');
      setToWarehouseId('');
      setQuantity('');
      setNotes('');
      setAvailableStock(0);
    },
    onError: (error: any) => {
      alert(`Transfer failed: ${error.response?.data?.message || error.message}`);
    },
  });

  const createWarehouseMutation = useMutation({
    mutationFn: (data: CreateWarehouseData) => stockMovementService.createWarehouse(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
      setShowWarehouseModal(false);
      setNewWarehouseName('');
      setNewWarehouseCode('');
      alert('Warehouse created successfully!');
    },
    onError: (error: any) => {
      alert(`Failed to create warehouse: ${error.response?.data?.message || error.message}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProductId || !fromWarehouseId || !toWarehouseId || !quantity) {
      alert('Please fill in all required fields');
      return;
    }
    if (fromWarehouseId === toWarehouseId) {
      alert('Source and destination warehouses must be different');
      return;
    }
    const qty = parseFloat(quantity);
    if (qty <= 0) { alert('Quantity must be greater than zero'); return; }
    if (qty > availableStock) { alert(`Insufficient stock. Available: ${availableStock} bottles`); return; }
    transferMutation.mutate({ productId: selectedProductId, fromWarehouseId, toWarehouseId, quantity: qty, notes: notes || undefined });
  };

  const handleCreateWarehouse = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newWarehouseName.trim() || !newWarehouseCode.trim()) {
      alert('Please fill in warehouse name and code');
      return;
    }
    createWarehouseMutation.mutate({ name: newWarehouseName.trim(), code: newWarehouseCode.trim().toUpperCase() });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Page header */}
      <div style={{ paddingBottom: 20, borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 400, letterSpacing: '0.4em', textTransform: 'uppercase', color: '#8a8778', marginBottom: 8 }}>
          Inventory
        </div>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 36, fontWeight: 300, color: '#1c2033', lineHeight: 1 }}>
          Stock Transfer
        </div>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 300, color: '#8a8778', marginTop: 8 }}>
          Transfer stock between warehouses
        </div>
      </div>

      <div style={{ maxWidth: 680 }}>
        <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: 28 }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Product */}
            <div>
              <label style={labelStyle}>Product (Oil Scent) <span style={{ color: '#c95a5a' }}>*</span></label>
              <select
                value={selectedProductId}
                onChange={(e) => { setSelectedProductId(e.target.value); setAvailableStock(0); }}
                style={inputStyle}
                disabled={isLoadingProducts}
                required
              >
                <option value="">Select a product...</option>
                {products.map((product: Product) => (
                  <option key={product.id} value={product.id}>
                    {product.name} ({product.sku})
                  </option>
                ))}
              </select>
            </div>

            {/* From Warehouse */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label style={{ ...labelStyle, marginBottom: 0 }}>From Warehouse <span style={{ color: '#c95a5a' }}>*</span></label>
                <button
                  type="button"
                  onClick={() => setShowWarehouseModal(true)}
                  style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#d97706', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  + New Warehouse
                </button>
              </div>
              <select
                value={fromWarehouseId}
                onChange={(e) => { setFromWarehouseId(e.target.value); setAvailableStock(0); }}
                style={inputStyle}
                disabled={isLoadingWarehouses}
                required
              >
                <option value="">Select source warehouse...</option>
                {warehouses.map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>{warehouse.name} ({warehouse.code})</option>
                ))}
              </select>
            </div>

            {/* To Warehouse */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label style={{ ...labelStyle, marginBottom: 0 }}>To Warehouse <span style={{ color: '#c95a5a' }}>*</span></label>
                <button
                  type="button"
                  onClick={() => setShowWarehouseModal(true)}
                  style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#d97706', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  + New Warehouse
                </button>
              </div>
              <select
                value={toWarehouseId}
                onChange={(e) => setToWarehouseId(e.target.value)}
                style={inputStyle}
                disabled={isLoadingWarehouses}
                required
              >
                <option value="">Select destination warehouse...</option>
                {warehouses.map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>{warehouse.name} ({warehouse.code})</option>
                ))}
              </select>
            </div>

            {/* Available Stock Info */}
            {selectedProductId && fromWarehouseId && (
              <div style={{ background: 'rgba(239,232,213,0.6)', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <svg width="16" height="16" fill="none" stroke="#d97706" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52' }}>
                  {isLoadingStock ? 'Loading stock...' : (
                    <>Available stock: <strong style={{ color: '#fbbf24' }}>{availableStock} bottles</strong> ({availableStock * 500}ML)</>
                  )}
                </span>
              </div>
            )}

            {/* Quantity */}
            <div>
              <label style={labelStyle}>Quantity (Bottles) <span style={{ color: '#c95a5a' }}>*</span></label>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                min="0.01"
                step="0.01"
                style={inputStyle}
                placeholder="Enter quantity in bottles"
                required
              />
              {quantity && (
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 4 }}>
                  = {parseFloat(quantity) * 500}ML
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <label style={labelStyle}>Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                style={{ ...inputStyle, resize: 'vertical' }}
                placeholder="Optional transfer notes..."
              />
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 8, borderTop: '1px solid rgba(92,78,51,0.15)' }}>
              <button
                type="button"
                onClick={() => { setSelectedProductId(''); setFromWarehouseId(''); setToWarehouseId(''); setQuantity(''); setNotes(''); setAvailableStock(0); }}
                style={{ padding: '10px 24px', fontFamily: "'Inter', sans-serif", fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', background: 'transparent', color: '#6b6a5c', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4, cursor: 'pointer' }}
              >
                Clear
              </button>
              <button
                type="submit"
                disabled={transferMutation.isPending}
                style={{ padding: '10px 24px', fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', background: 'linear-gradient(135deg, #d97706, #b45309)', color: '#1c2033', border: 'none', borderRadius: 4, cursor: transferMutation.isPending ? 'not-allowed' : 'pointer', opacity: transferMutation.isPending ? 0.7 : 1 }}
              >
                {transferMutation.isPending ? 'Transferring...' : 'Transfer Stock'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Warehouse Creation Modal */}
      {showWarehouseModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(7,7,15,0.85)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 8, padding: 32, maxWidth: 440, width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 300, color: '#1c2033' }}>
                Create New Warehouse
              </div>
              <button
                type="button"
                onClick={() => { setShowWarehouseModal(false); setNewWarehouseName(''); setNewWarehouseCode(''); }}
                style={{ background: 'none', border: 'none', color: '#6b6a5c', cursor: 'pointer', padding: 4 }}
              >
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreateWarehouse} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={labelStyle}>Warehouse Name <span style={{ color: '#c95a5a' }}>*</span></label>
                <input
                  type="text"
                  value={newWarehouseName}
                  onChange={(e) => setNewWarehouseName(e.target.value)}
                  style={inputStyle}
                  placeholder="e.g. Main Warehouse"
                  required
                />
              </div>
              <div>
                <label style={labelStyle}>Warehouse Code <span style={{ color: '#c95a5a' }}>*</span></label>
                <input
                  type="text"
                  value={newWarehouseCode}
                  onChange={(e) => setNewWarehouseCode(e.target.value.toUpperCase())}
                  style={inputStyle}
                  placeholder="e.g. WH-001"
                  required
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 8, borderTop: '1px solid rgba(92,78,51,0.15)' }}>
                <button
                  type="button"
                  onClick={() => { setShowWarehouseModal(false); setNewWarehouseName(''); setNewWarehouseCode(''); }}
                  style={{ padding: '10px 20px', fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', background: 'transparent', color: '#6b6a5c', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createWarehouseMutation.isPending}
                  style={{ padding: '10px 20px', fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500, letterSpacing: '0.18em', textTransform: 'uppercase', background: 'linear-gradient(135deg, #d97706, #b45309)', color: '#1c2033', border: 'none', borderRadius: 4, cursor: createWarehouseMutation.isPending ? 'not-allowed' : 'pointer', opacity: createWarehouseMutation.isPending ? 0.7 : 1 }}
                >
                  {createWarehouseMutation.isPending ? 'Creating...' : 'Create Warehouse'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
